import { Link } from 'react-router-dom';
import { Edit } from 'lucide-react';
import { sanitizeHtmlForPreview } from '../../../../utils/helper';
import {
  getSendingBehaviorLabel,
  getSubscriptionPreferenceLabel,
} from '../../../../utils/workflowNodeHelpers';

export interface EmailTemplate {
  name?: string;
  unlayerConfig?: { html?: string };
  subject?: string;
}

export interface EmailNodeProps {
  /** Template ID for navigation */
  templateId?: string | null;
  /** Loaded email template (name, html) */
  emailTemplate?: EmailTemplate | null;
  /** Whether template is still loading */
  emailTemplateLoading?: boolean;
  /** Sending behavior value from config */
  sendingBehavior?: string | null;
  /** Subscription preference value from config */
  subscriptionPreference?: string | null;
  /** Message limit value from config */
  messageLimit?: string | null;
  /** Unsubscribe groups for resolving subscription labels */
  unsubscribeGroups?: Array<{ _id?: string; title: string }>;
  /** Email builder path for Edit and Create Content */
  contentTo?: string;
}

const PREVIEW_CARD_CLASS =
  'overflow-hidden pointer-events-none border-2 border-dashed border-border rounded-lg bg-muted/30';

const EmailNode = ({
  templateId,
  emailTemplate,
  emailTemplateLoading,
  sendingBehavior,
  subscriptionPreference,
  messageLimit,
  unsubscribeGroups = [],
  contentTo,
}: EmailNodeProps) => {
  const hasEmailTemplate = Boolean(emailTemplate && templateId);
  const hasPreview =
    hasEmailTemplate && Boolean(emailTemplate?.unlayerConfig?.html);
  const hasSettings = Boolean(
    sendingBehavior || subscriptionPreference || messageLimit
  );
  const subjectLabel =
    hasEmailTemplate && emailTemplate?.subject
      ? emailTemplate.subject
      : 'No subject';

  return (
    <div className="flex flex-col gap-4 rounded-lg overflow-hidden">
      {/* Email content section */}
      <section
        className="flex flex-col gap-3 px-4 pb-4"
        aria-label="Email content"
      >
        {/* Subject row: label + value + Edit button */}
        <div className="flex flex-col gap-2">
          {hasEmailTemplate && (
            <div className="flex items-start justify-between gap-2 min-w-0">
              <div className="flex-1 min-w-0">
                <p className="text-[10px] font-medium text-muted-foreground uppercase tracking-wider mb-0.5">
                  Subject
                </p>
                <p
                  className="text-sm font-semibold text-foreground truncate"
                  title={subjectLabel}
                >
                  {emailTemplateLoading ? (
                    <span className="inline-block h-4 bg-muted rounded animate-pulse w-32" />
                  ) : (
                    subjectLabel
                  )}
                </p>
              </div>
              {contentTo && (
                <Link
                  to={contentTo}
                  onClick={(e) => e.stopPropagation()}
                  aria-label="Edit email content in visual editor"
                  className="nodrag nopan flex-shrink-0 inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md border border-border bg-background text-foreground text-xs font-medium hover:bg-muted hover:border-primary/30 transition-colors"
                >
                  <Edit className="w-3 h-3 shrink-0" aria-hidden />
                  <span>Edit</span>
                </Link>
              )}
            </div>
          )}

          {/* Email preview thumbnail */}
          {emailTemplateLoading ? (
            <div className="flex flex-col gap-2">
              <div className="h-20 bg-muted rounded-lg animate-pulse" />
              <div className="h-3 bg-muted rounded animate-pulse w-24 mx-auto" />
            </div>
          ) : hasPreview ? (
            <div
              className={`${PREVIEW_CARD_CLASS} p-2 opacity-60`}
              style={{ maxHeight: '160px' }}
            >
              <div
                dangerouslySetInnerHTML={{
                  __html: sanitizeHtmlForPreview(
                    emailTemplate!.unlayerConfig!.html!
                  ),
                }}
                className="origin-top-left"
                style={{
                  transform: 'scale(0.55)',
                  width: '100%',
                  height: '100%',
                }}
              />
            </div>
          ) : (
            <div
              className={`${PREVIEW_CARD_CLASS} min-h-[100px] flex flex-col items-center justify-center p-4 text-center`}
              aria-label="No email content"
            >
              <p className="text-sm text-muted-foreground">
                Email preview thumbnail
              </p>
              <p className="text-xs text-muted-foreground mt-1">
                No email content yet
              </p>
              {contentTo && (
                <Link
                  to={contentTo}
                  onClick={(e) => e.stopPropagation()}
                  className="nodrag nopan mt-3 inline-flex items-center gap-2 px-3 py-1.5 bg-primary text-primary-foreground rounded-lg text-xs font-medium hover:opacity-90 transition-opacity shadow-sm pointer-events-auto"
                >
                  <Edit className="w-3 h-3 shrink-0" aria-hidden />
                  <span>Create Content</span>
                </Link>
              )}
            </div>
          )}
        </div>

        {/* Settings section */}
        {hasSettings && (
          <section
            className="flex flex-col gap-3 pt-1"
            aria-label="Email settings"
          >
            <div className="relative" role="separator" aria-hidden>
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-border" />
              </div>
              <div className="relative flex justify-center">
                <span className="bg-card px-2 text-[10px] font-medium text-muted-foreground uppercase tracking-wider">
                  Settings
                </span>
              </div>
            </div>
            <ul className="space-y-2.5 list-none p-0 m-0">
              {sendingBehavior && (
                <li className="flex items-center justify-between gap-3 py-1">
                  <span className="text-xs text-muted-foreground">
                    Sending Behavior
                  </span>
                  <span className="text-xs font-semibold text-foreground text-right">
                    {getSendingBehaviorLabel(sendingBehavior)}
                  </span>
                </li>
              )}
              {subscriptionPreference && (
                <li className="flex items-center justify-between gap-3 py-1">
                  <span className="text-xs text-muted-foreground flex-shrink-0">
                    Subscription
                  </span>
                  <span className="text-xs font-semibold text-foreground text-right min-w-0 line-clamp-2">
                    {getSubscriptionPreferenceLabel(
                      subscriptionPreference,
                      unsubscribeGroups
                    )}
                  </span>
                </li>
              )}
            </ul>
          </section>
        )}
      </section>
    </div>
  );
};

export default EmailNode;
